/**
 * Preferred edit and the Low/High override flow (contracts-plan.md §7.1,
 * §7.4, R14; §11 tab 3, §11 Global — "every number shows its label and
 * provenance on hover").
 *
 * Low and High are COMPUTED: typing over one shows the warning ("this value
 * is computed; the computed value is kept and both are shown", §7.4 verbatim)
 * and needs a reason — the write is warn-and-keep, never a replace, so the
 * caller (CardView) merges the override BESIDE the computed number rather
 * than over it. Preferred is already maintained by a person, so no warning —
 * just the audited write.
 */
import { useState } from 'react';
import { Modal, primaryBtnCls, secondaryBtnCls, fieldCls } from '../../../ui';
import { setOverride, setPreferred } from '../../api/rateCard';
import type { RcOverrideField, OverrideResult, SetPreferredResult } from '../../api/rateCard';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  valueId: string;
  field: RcOverrideField;
  computedValue: number | null;
  currentPreferred: number | null;
  onOverrideSaved: (result: OverrideResult) => void;
  onPreferredSaved: (result: SetPreferredResult) => void;
}

const FIELD_LABEL: Record<RcOverrideField, string> = { low: 'Low', preferred: 'Preferred', high: 'High' };

export function ValueEditModal({
  isOpen,
  onClose,
  valueId,
  field,
  computedValue,
  currentPreferred,
  onOverrideSaved,
  onPreferredSaved,
}: Props) {
  const isComputed = field === 'low' || field === 'high';
  const [value, setValue] = useState(() => (field === 'preferred' && currentPreferred !== null ? String(currentPreferred) : ''));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);

    if (!isComputed) {
      setSaving(true);
      try {
        const parsed = value.trim() === '' ? null : Number(value);
        if (parsed !== null && Number.isNaN(parsed)) {
          setError('Enter a number, or leave it blank to clear the preferred value.');
          setSaving(false);
          return;
        }
        const result = await setPreferred(valueId, parsed);
        onPreferredSaved(result);
        onClose();
      } catch {
        setError('Could not save the preferred value.');
      } finally {
        setSaving(false);
      }
      return;
    }

    const parsed = Number(value);
    if (value.trim() === '' || Number.isNaN(parsed)) {
      setError('Enter a number.');
      return;
    }
    if (reason.trim() === '') {
      setError('A reason is required (§7.4: who, what, when and why).');
      return;
    }
    setSaving(true);
    try {
      const result = await setOverride(valueId, { field, value: parsed, reason: reason.trim() });
      onOverrideSaved(result);
      onClose();
    } catch {
      setError('Could not save the override.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit ${FIELD_LABEL[field]}`} maxWidth="max-w-md">
      <div data-testid="rc-value-modal" className="space-y-3">
        {isComputed && (
          <p
            data-testid="rc-override-warning"
            className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
          >
            This value is computed{computedValue !== null ? ` — it is currently ${computedValue}` : ''}. The computed
            value is kept, and both will be shown (§7.4).
          </p>
        )}

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">
            {isComputed ? 'Override value' : 'Preferred value'}
          </span>
          <input
            type="number"
            data-testid="rc-value-input"
            className={`${fieldCls} w-full`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>

        {isComputed && (
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Reason</span>
            <textarea
              data-testid="rc-reason-input"
              className={`${fieldCls} w-full`}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        )}

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            data-testid="rc-value-submit"
            className={primaryBtnCls}
            onClick={handleSave}
            disabled={saving}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ValueEditModal;
